import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatCents, type Department, type Position } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank } from '@/lib/format';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ActiveBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Departamentos e cargos.
 *
 * Duas listas curtas na mesma tela: separá-las em dois itens de menu obrigaria
 * o operador a ir e voltar para cadastrar um cargo novo num departamento novo.
 */
export function CargosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [creatingDepartment, setCreatingDepartment] = useState(false);
  const [creatingPosition, setCreatingPosition] = useState(false);

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<{ data: Department[] }>('/departments'),
  });

  const positionsQuery = useQuery({
    queryKey: ['positions'],
    queryFn: () => api.get<{ data: Position[] }>('/positions'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['departments'] });
    void queryClient.invalidateQueries({ queryKey: ['positions'] });
  };

  const departmentColumns: Array<Column<Department>> = [
    { key: 'name', label: 'Departamento', strong: true, render: (row) => row.name },
    { key: 'description', label: 'Descrição', render: (row) => blank(row.description) },
    {
      key: 'employeeCount',
      label: 'Colaboradores',
      right: true,
      mono: true,
      render: (row) => row.employeeCount.toLocaleString('pt-BR'),
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
  ];

  const positionColumns: Array<Column<Position>> = [
    { key: 'name', label: 'Cargo', strong: true, render: (row) => row.name },
    { key: 'departmentName', label: 'Departamento', render: (row) => blank(row.departmentName) },
    {
      key: 'baseSalaryCents',
      label: 'Salário base',
      right: true,
      mono: true,
      render: (row) => formatCents(row.baseSalaryCents),
    },
    {
      key: 'employeeCount',
      label: 'Colaboradores',
      right: true,
      mono: true,
      render: (row) => row.employeeCount.toLocaleString('pt-BR'),
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Departamentos e cargos"
      />

      <div className="workspace__body">
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700 }}>Departamentos</h2>
            <div style={{ flex: 1 }} />
            {can('departamentos:criar') && (
              <Button small icon={Plus} onClick={() => setCreatingDepartment(true)}>
                Novo departamento
              </Button>
            )}
          </div>

          {departmentsQuery.isPending && <TableSkeleton columns={4} rows={3} />}
          {departmentsQuery.isError && (
            <ErrorState
              message={errorMessage(departmentsQuery.error)}
              onRetry={() => void departmentsQuery.refetch()}
            />
          )}
          {departmentsQuery.data && (
            <SmartDataTable
              caption="Departamentos"
              columns={departmentColumns}
              rows={departmentsQuery.data.data}
              emptyState={
                <EmptyState
                  title="Nenhum departamento"
                  description="Obra, administrativo, manutenção — comece pelos que já existem."
                />
              }
            />
          )}
        </section>

        <section>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700 }}>Cargos</h2>
            <div style={{ flex: 1 }} />
            {can('departamentos:criar') && (
              <Button small icon={Plus} onClick={() => setCreatingPosition(true)}>
                Novo cargo
              </Button>
            )}
          </div>

          {positionsQuery.isPending && <TableSkeleton columns={5} rows={3} />}
          {positionsQuery.isError && (
            <ErrorState
              message={errorMessage(positionsQuery.error)}
              onRetry={() => void positionsQuery.refetch()}
            />
          )}
          {positionsQuery.data && (
            <SmartDataTable
              caption="Cargos"
              columns={positionColumns}
              rows={positionsQuery.data.data}
              emptyState={
                <EmptyState
                  title="Nenhum cargo"
                  description="Pedreiro, servente, encarregado — o salário base entra na folha."
                />
              }
            />
          )}
        </section>
      </div>

      <DepartmentModal
        open={creatingDepartment}
        onClose={() => setCreatingDepartment(false)}
        onSaved={() => {
          setCreatingDepartment(false);
          refresh();
        }}
      />

      <PositionModal
        open={creatingPosition}
        departments={departmentsQuery.data?.data ?? []}
        onClose={() => setCreatingPosition(false)}
        onSaved={() => {
          setCreatingPosition(false);
          refresh();
        }}
      />
    </>
  );
}

function DepartmentModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName('');
      setDescription('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/departments', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo departamento"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({ name: name.trim(), description: description.trim() || null })
            }
          >
            Criar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field label="Nome" htmlFor="dep-name" error={errors.name}>
          <input
            id="dep-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Obra"
          />
        </Field>
        <Field label="Descrição" htmlFor="dep-desc">
          <textarea
            id="dep-desc"
            className="input"
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

function PositionModal({
  open,
  departments,
  onClose,
  onSaved,
}: {
  open: boolean;
  departments: Department[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [baseSalaryCents, setBaseSalaryCents] = useState<number | null>(null);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName('');
      setDepartmentId('');
      setBaseSalaryCents(null);
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post('/positions', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo cargo"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                name: name.trim(),
                departmentId: departmentId || null,
                baseSalaryCents: baseSalaryCents ?? 0,
              })
            }
          >
            Criar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field label="Nome do cargo" htmlFor="pos-name" error={errors.name}>
          <input
            id="pos-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Pedreiro"
          />
        </Field>
        <Field label="Departamento" htmlFor="pos-dep">
          <select
            id="pos-dep"
            className="input"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
          >
            <option value="">Sem departamento</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Salário base"
          htmlFor="pos-salary"
          hint="Sugerido ao admitir um colaborador neste cargo"
        >
          <MoneyInput id="pos-salary" value={baseSalaryCents} onChange={setBaseSalaryCents} />
        </Field>
      </FormSection>
    </Modal>
  );
}
